/* buildCFSLinesForCompany — Phase 3a chart-of-accounts builder for CFS_LINES.

   Derives the per-company CFS_LINES array from the BS leaf set (bs_actuals_meta
   + module-gated synthetics) plus SYNTHETIC_CFS_LINES (oper/wc/invest/fin
   skeleton, oneoffs, totals, formulas, mm_sweep, beg_cash/end_cash).

   Activity-line generation by (category, role):

     category=current_assets        →  chg_<cfs_id>  in working capital, sign='a'
     category=current_liabilities   →  chg_<cfs_id>  in working capital, sign='l'
     category=non_current_assets    →  inv_<cfs_id>  in investing,        sign='a'
     category=non_current_liabilities → fin_<cfs_id> in financing,        sign='l'
     category=equity                →  fin_<cfs_id>  in financing,        sign='l'
     role=short_term_debt (a current liability) -> fin_<cfs_id> in financing, sign='l'

   CFS_EXCLUDED_ROLES (cash/clearing/sweep_target, accumulated_depreciation/
   amortization, retained_earnings, net_income) are skipped — they are
   captured by the cash reconciliation, the D&A add-back, and the net_income
   IS line respectively.

   Each meta entry that flows into CFS provides:
     - cfs_id: explicit ID override (e.g. "inv_comp" instead of the default
       "inv_computers"). Required for byte-identical Semitek equivalence.
       Falls back to "<prefix>_<bs_id>" when absent (future tenants).
     - cfs_label: full label override (e.g. "(Increase)/Decrease A/R - Stripe"
       where the BS label "Accounts Receivable - Stripe" doesn't fit the CFS
       abbreviation convention). Falls back to a generated label.

   No engines consume this builder yet; Phase 3a only validates equivalence
   for Semitek.
*/

import { SYNTHETIC_BS_LINES, SYNTHETIC_CFS_LINES, CFS_EXCLUDED_ROLES, BS_ROLES } from './lineCategories';

// Session 0: per-module-type config for synthetic deferred-revenue line emission.
// Mirror of MODULE_TYPE_TO_BS_CONFIG in buildBSLines.js — kept in sync so
// CFS working-cap lines align with the BS structure produced there.
const MODULE_TYPE_TO_CFS_CONFIG = {
  ae_quota:      { refKey: 'defrev_ae_quota',      cfsId: 'chg_dr_ae_quota',      sortOrder: 610 },
  company_quota: { refKey: 'defrev_company_quota', cfsId: 'chg_dr_company_quota', sortOrder: 620 },
};

function moduleEnabledForCFS(ctx, moduleType) {
  if (moduleType === 'ae_quota')      return !!ctx.aeQuotaEnabled;
  if (moduleType === 'company_quota') return !!ctx.companyQuotaEnabled;
  return false;
}

function classifyForCFS(leaf) {
  if (CFS_EXCLUDED_ROLES.has(leaf.role)) return null;
  // Short-term debt stays a CURRENT liability on the balance sheet but is a FINANCING flow:
  // borrowing and repayment are not working capital. The role wins over the category here
  // and nowhere else. inferBsMeta gives the same leaf a fin_ cfs_id, which is what the
  // cash_avail sum (computeCFS) and the Step-4b bridge key on.
  if (leaf.role === BS_ROLES.SHORT_TERM_DEBT) return { section: 'fin', sign: 'l', prefix: 'fin' };
  switch (leaf.category) {
    case 'current_assets':         return { section: 'wc',     sign: 'a', prefix: 'chg' };
    case 'current_liabilities':    return { section: 'wc',     sign: 'l', prefix: 'chg' };
    case 'non_current_assets':     return { section: 'invest', sign: 'a', prefix: 'inv' };
    case 'non_current_liabilities':return { section: 'fin',    sign: 'l', prefix: 'fin' };
    case 'equity':                 return { section: 'fin',    sign: 'l', prefix: 'fin' };
    default:                       return null;
  }
}

/* ★ EXPORTED, and EXTRACTED verbatim from buildCFSLinesForCompany's body, for the same reason as
 * generateLabel below: the Step-4b review screen names the same two accounts and must not derive
 * them a second time. Returns exactly what the two inlined consts returned, including the
 * 'Money Market' / 'Total Clearing' fallbacks — behaviour-identical for every tenant. */
export function cfsCashLabels(bsActualsMeta) {
  const meta = bsActualsMeta || {};
  const sweepTargetId = Object.keys(meta).find(id => meta[id] && meta[id].role === 'sweep_target');
  return {
    sweepLabel: (sweepTargetId && meta[sweepTargetId].label) || 'Money Market',
    clearingLabel: (meta.total_clearing && meta.total_clearing.display_label) || 'Total Clearing',
  };
}

/* ★ EXPORTED for the Step-4b review-screen bridge (computeHistoricalFinancials).
 * That screen recomputes the historical CFS from the raw GL and used to label every row
 * "Δ <name>", while this model — the live Cash Flow page — words the same rows by category.
 * The bridge now calls THIS function rather than carrying a second copy of the four rules,
 * so the review screen and the statement cannot drift apart (§12.5 one-matcher rule).
 * Pure: (leaf, cls) → string. No behaviour change to this module; only the `export`. */
export function generateLabel(leaf, cls) {
  if (leaf.cfs_label) return leaf.cfs_label;
  if (cls.prefix === 'chg' && cls.sign === 'a') return `(Increase)/Decrease ${leaf.label}`;
  if (cls.prefix === 'chg' && cls.sign === 'l') return `Increase/(Decrease) ${leaf.label}`;
  if (cls.prefix === 'inv')                     return `(Purchase of) ${leaf.label}`;
  if (cls.prefix === 'fin')                     return `Increase/(Decrease) ${leaf.label}`;
  return leaf.label;
}

function makeBsSourceLine(leaf, cls) {
  const cfsId = leaf.cfs_id || `${cls.prefix}_${leaf.id}`;
  return {
    id: cfsId,
    label: generateLabel(leaf, cls),
    level: cls.section === 'wc' ? 2 : 1,
    source: 'bs',
    bsKey: leaf.id,
    sign: cls.sign,
  };
}

function makeAlwaysPresent(id) {
  return { id, ...SYNTHETIC_CFS_LINES[id] };
}

// ── One-off CFS lines (cfs_oneoffs) ────────────────────────────────────────
// A one-off belongs to the tenant that HAS it and never transfers: the line is
// emitted only when its key is present in THIS tenant's cfs_oneoffs blob.
// Previously ppp_loan / loan_writeoff were pushed unconditionally, so Semitek's
// COVID-era PPP loan appeared (at $0.00) on every tenant's CFS.
//
// NOTE: fa_writeoff is NOT a member of this set. It is a DERIVED always-present
// line (−Σ of gross fixed-asset month-over-month drops); cfs_oneoffs.fa_writeoff
// is only its per-month OVERRIDE. It is excluded here and emitted separately.
const ONEOFF_LABELS = {
  ppp_loan:      'PPP Loan Forgiveness',
  loan_writeoff: 'Loan Write-Off',
};
// Canonical emission order for known one-offs. Unknown keys follow, sorted, so
// line order never depends on JSONB key order.
const ONEOFF_ORDER = ['ppp_loan', 'loan_writeoff'];
const DERIVED_ONEOFF_KEYS = new Set(['fa_writeoff']);

// Auto-titlecase fallback for one-off keys with no known label (mirrors the
// slug→label fallback in lineRoles.js) — next-tenant-safe for unknown one-offs.
function titleCaseSlug(slug) {
  return String(slug)
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function buildOneoffLines(cfsOneoffs) {
  const keys = Object.keys(cfsOneoffs || {}).filter(k => !DERIVED_ONEOFF_KEYS.has(k));
  const known = ONEOFF_ORDER.filter(k => keys.includes(k));
  const unknown = keys.filter(k => !ONEOFF_ORDER.includes(k)).sort();
  return [...known, ...unknown].map(id => ({
    id,
    label: ONEOFF_LABELS[id] || titleCaseSlug(id),
    level: 1,
    source: 'oneoff',
  }));
}

export function buildCFSLinesForCompany(bsActualsMeta, revModuleMappings, moduleContext, cfsOneoffs) {
  const meta = bsActualsMeta || {};
  const ctx = moduleContext || {};

  // ── Tenant cash-account labels (Session A display_label pattern) ──────────
  // The CFS cash reconciliation names TWO distinct accounts:
  //   clearingLabel — the operating-cash rollup (total_clearing), which EXCLUDES
  //                   the swept account. Overridable per tenant via
  //                   bs_actuals_meta['total_clearing'].display_label
  //                   (AE Model: "Total Cash"; Semitek: no override → "Total Clearing").
  //   sweepLabel    — the sweep-target account itself (role='sweep_target'), whose
  //                   movement books as a FINANCING flow (mm_sweep), which is why the
  //                   reconciliation reads "<clearing> Less <sweep>".
  // These reproduce Semitek's previously-hardcoded strings BY CONSTRUCTION
  // ("Total Clearing" / "Chase Money Market") while giving AE Model
  // "Total Cash" / "First Republic Bank". Labels are never read by computeCFS
  // (it dispatches on id/source/sign/bsKey/sumOf/type) → label-only, no numeric effect.
  const { sweepLabel, clearingLabel } = cfsCashLabels(meta);

  // Per-tenant label overrides applied to COPIES (makeAlwaysPresent spreads
  // SYNTHETIC_CFS_LINES[id] into a fresh object) — the module-level const is
  // never mutated, so one tenant's labels cannot leak into the next.
  const withLabel = (id, label) => ({ ...makeAlwaysPresent(id), label });

  // Same leaf set as buildBSLines: meta + enabled module-gated synthetics +
  // dynamic DR synthetics derived from revenue_module_mappings (Session 0).
  // Must mirror buildBSLines.js exactly so the CFS chg_dr_* lines align with
  // the BS deferred_rev_* lines.
  const metaLeaves = Object.entries(meta).map(([id, m]) => ({ id, ...m }));
  const moduleLeaves = SYNTHETIC_BS_LINES.moduleGated
    .filter(g => g.enabledCheck(ctx))
    .map(g => ({ ...g }));

  // Session 0: data-driven DR synthetics. For each revMappings row whose
  // bs_line_key is NOT in bs_actuals_meta, emit a synthetic CFS source leaf
  // (the BS-side dynamic synthetic was emitted in buildBSLines; here we
  // mirror it so the working-cap CFS line gets generated). For revMappings
  // rows whose bs_line_key IS in meta, the historical meta entry already
  // flows through classifyForCFS via metaLeaves — no synthetic needed.
  const dynDRSynthetics = [];
  for (const row of (revModuleMappings || [])) {
    if (!row.bs_line_key) continue;
    const cfg = MODULE_TYPE_TO_CFS_CONFIG[row.module_type];
    if (!cfg) continue;
    if (!moduleEnabledForCFS(ctx, row.module_type)) continue;
    if (row.bs_line_key in meta) continue;
    dynDRSynthetics.push({
      id: row.bs_line_key,
      label: 'Deferred Revenue - ' + (row.module_name || row.display_name || row.module_type),
      level: 2,
      category: 'current_liabilities',
      subcategory: 'deferred_revenue',
      role: 'deferred_revenue',
      display_order: cfg.sortOrder,
      cfs_id: cfg.cfsId,
    });
  }

  const allLeaves = [...metaLeaves, ...moduleLeaves, ...dynDRSynthetics]
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  // fa_writeoff derive source: gross fixed-asset leaf ids. Gross FA lines only rise
  // (purchases); depreciation hits the separate accumdep contra — so any month-over-month
  // gross DROP is a disposal/write-off. computeCFS derives −Σ max(0, prev−curr) over these
  // keys (overridable per-month via cfs_oneoffs.fa_writeoff for sales / partial-NBV cases).
  // Intangibles (role='intangible_asset') are intentionally excluded — this is "Write off of
  // Fixed Assets". Tenants with no FA drops derive 0 (byte-identical).
  const faGrossKeys = allLeaves.filter(l => l.role === 'fixed_asset').map(l => l.id);

  // ── Fail-loud guard: empty total_clearing rollup ─────────────────────────
  // computeCFS keys the sweep's operating-cash basis on the `total_clearing`
  // rollup (bv("total_clearing", m)). buildBSLines now selects that rollup's members
  // by ROLE (cash|clearing) within current_assets. An empty rollup ⇒ $0 cash basis ⇒
  // a silently broken sweep (the Session-A failure: AE Model's $39.67M sat outside
  // the engine). Warn loudly; never throw, never change a number.
  //
  // Post-generalization the ONLY way to be cash-roled yet outside the rollup is to
  // carry the wrong `category` — so that is what this now checks. (Before the role
  // predicate landed, the trap was subcategory ≠ 'clearing'; that can no longer
  // empty the rollup.)
  const cashLeaves = allLeaves.filter(l => l.role === 'cash' || l.role === 'clearing');
  const rollupLeaves = cashLeaves.filter(l => l.category === 'current_assets');
  if (cashLeaves.length > 0 && rollupLeaves.length === 0) {
    console.warn(
      '[buildCFSLines] Sweep cash basis is EMPTY: this tenant has ' + cashLeaves.length +
      " cash/clearing leaf(s) but NONE carry category='current_assets', so the total_clearing " +
      'rollup sums to $0 and the sweep will be silently wrong. Offending leaves: ' +
      cashLeaves.map(l => l.id + ' (role=' + l.role + ', category=' + l.category + ')').join(', ')
    );
  }

  // ── Transition canary ────────────────────────────────────────────────────
  // The role-predicate swap is byte-identical ONLY while the two classifications
  // agree — true for every tenant onboarded to date (and for build-B tenants, which
  // write both role AND subcategory='clearing'). But the POINT of decoupling is that
  // a future/hand-edited tenant may carry role='cash' with subcategory='cash'. If the
  // two sets ever diverge, the BS cash section moved relative to what the old
  // subcategory predicate would have produced — flag it once, loudly, so the change is
  // never silent. Detection only; no number is touched here.
  const legacySet = new Set(
    allLeaves.filter(l => l.category === 'current_assets' && l.subcategory === 'clearing').map(l => l.id)
  );
  const roleSet = new Set(rollupLeaves.map(l => l.id));
  if (legacySet.size > 0 && roleSet.size > 0) {
    const onlyRole = [...roleSet].filter(id => !legacySet.has(id));
    const onlyLegacy = [...legacySet].filter(id => !roleSet.has(id));
    if (onlyRole.length || onlyLegacy.length) {
      console.warn(
        '[buildCFSLines] Cash-rollup predicate divergence (role vs legacy subcategory). ' +
        'The operating-cash basis now follows ROLE. ' +
        (onlyRole.length ? 'Newly IN the rollup (role=cash|clearing, not subcategory=clearing): ' + onlyRole.join(', ') + '. ' : '') +
        (onlyLegacy.length ? 'Newly OUT (subcategory=clearing but not a cash role): ' + onlyLegacy.join(', ') + '. ' : '') +
        'Verify the Total Cash balance against the tenant\'s actual bank balance.'
      );
    }
  }

  // One-off lines, gated on THIS tenant's cfs_oneoffs data (see buildOneoffLines).
  const oneoffLines = buildOneoffLines(cfsOneoffs);

  // Bucket into CFS sections (working-cap assets / liabilities, invest, fin)
  const wcAssets = []; // chg_*, sign='a'
  const wcLiab   = []; // chg_*, sign='l'
  const invest   = []; // inv_*
  const fin      = []; // fin_*

  for (const leaf of allLeaves) {
    const cls = classifyForCFS(leaf);
    if (!cls) continue;
    const entry = makeBsSourceLine(leaf, cls);
    if (cls.section === 'wc' && cls.sign === 'a') wcAssets.push(entry);
    else if (cls.section === 'wc' && cls.sign === 'l') wcLiab.push(entry);
    else if (cls.section === 'invest') invest.push(entry);
    else if (cls.section === 'fin') fin.push(entry);
  }

  // Working-capital lines: assets first (in display_order), then liabilities
  const wcLines = [...wcAssets, ...wcLiab];

  const lines = [];

  // ─── OPERATING ───────────────────────────────────────────────────────
  lines.push(makeAlwaysPresent('oper_header'));
  lines.push(makeAlwaysPresent('net_income'));
  lines.push(makeAlwaysPresent('da_addback'));
  lines.push({ ...makeAlwaysPresent('fa_writeoff'), gross_keys: faGrossKeys });
  for (const l of oneoffLines) lines.push(l);
  lines.push(makeAlwaysPresent('wc_header'));
  for (const l of wcLines) lines.push(l);
  lines.push({ ...makeAlwaysPresent('total_chg_wc'), sumOf: wcLines.map(l => l.id) });
  lines.push({
    ...makeAlwaysPresent('total_oper'),
    // sumOf built from the one-off lines ACTUALLY EMITTED — not a hardcoded id list.
    sumOf: ['net_income', 'da_addback', 'fa_writeoff', ...oneoffLines.map(l => l.id), 'total_chg_wc'],
  });

  // ─── INVESTING ───────────────────────────────────────────────────────
  lines.push(makeAlwaysPresent('s1'));
  lines.push(makeAlwaysPresent('invest_header'));
  for (const l of invest) lines.push(l);
  lines.push({ ...makeAlwaysPresent('total_invest'), sumOf: invest.map(l => l.id) });

  // ─── FINANCING ───────────────────────────────────────────────────────
  lines.push(makeAlwaysPresent('s2'));
  lines.push(makeAlwaysPresent('fin_header'));
  for (const l of fin) lines.push(l);
  lines.push(withLabel('cash_avail', `Cash Available/(Required) Before ${sweepLabel} Sweep`));
  lines.push(makeAlwaysPresent('s_sw'));
  lines.push(withLabel('mm_sweep', `${sweepLabel} (Replenishment) / Drawdown`));
  lines.push({ ...makeAlwaysPresent('total_fin'), sumOf: [...fin.map(l => l.id), 'mm_sweep'] });

  // ─── CASH RECONCILIATION ─────────────────────────────────────────────
  lines.push(makeAlwaysPresent('s3'));
  lines.push(withLabel('beg_cash', `Beginning ${clearingLabel} Less ${sweepLabel}`));
  lines.push(makeAlwaysPresent('chg_cash'));
  lines.push(withLabel('end_cash', `Ending ${clearingLabel} Less ${sweepLabel}`));

  return lines;
}
